import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PackageModel } from '@data-layer/models/lcl';
import { enumActivityItem, enumPackageType, enumPalletStandard } from '@data-layer/enums/lcl';
import { TranslateService } from '@ngx-translate/core';
import { enumLengthUnit, enumWeightUnit } from '@data-layer/enums/common';
import { ManagementPackageModel } from '@data-layer/models/management';
import { precisionRound } from '@helpers/data-transform.helper';
import { UserModel } from '@data-layer/models/user';
import { METRE_CENTIMETER_RATIO } from '@constants/constants';

@Component({
  selector: 'app-lcl-packages-view-row',
  template: `
    <div class="lcl-package-row" [ngClass]="{ outdated: isOutdated || isRemoved, 'is-admin': isAdminView }">
      <div class="lcl-package-col-icon-box my-auto">
        <img [src]="isShowPallet ? 'assets/icons/icon_pallet.svg' : 'assets/icons/icon_package.svg'" />
      </div>

      <div class="lcl-package-col-package my-auto">
        {{ package.quantity }}
        {{ typeName }}
        <span *ngIf="isShowPallet" class="d-none d-xl-inline ml-2" [title]="palletStandard">({{ palletStandard }})</span>
      </div>

      <div class="lcl-package-col-dimensions my-auto">
        <span *ngIf="!isShowPalletStandardInput">{{ normalizedLength }} X {{ normalizedWidth }} X</span>
        {{ normalizedHeight }} {{ lengthName }}
      </div>

      <div class="lcl-package-col-weight my-auto">{{ package.weight }} {{ weightName }}</div>

      <div class="lcl-package-col-icon-trash" *ngIf="isCanDelete">
        <div class="lcl-package-col-icon-trash-wrapper" (click)="removePackage.emit(package.id)">
          <i class="fa fa-trash"></i>
        </div>
      </div>

      <div class="lcl-package-col-notes" *ngIf="!isNotModified && (!isOutdated || isRemoved)">
        <span class="lcl-package-col-notes-non-stackable" *ngIf="!package.stackable">{{ 'LABELS.NON_STACKABLE' | translate }}</span>
        <span class="lcl-package-col-notes-action">{{ 'PACKAGE_UPDATE_STATUS.' + enumActivityItem[package?.action] | translate }}</span>
        <span class="lcl-package-col-notes-modification-data">
          {{ 'LABELS.IN' | translate }} {{ package?.modificationData?.modifiedDate | customDateByUtc }} {{ 'LABELS.BY' | translate }}
          {{ user?.firstName + ' ' + user?.lastName }}
        </span>
      </div>
    </div>
  `,
  styleUrls: ['./lcl-packages-view-row.component.scss'],
})
export class LclPackagesViewRowComponent {
  @Input() public package: PackageModel | ManagementPackageModel;
  @Input() public isCanDelete: boolean;
  @Input() public isAdminView: boolean;
  @Input() public activityItem: number;
  @Input() public user: UserModel;
  @Output() public removePackage: EventEmitter<number> = new EventEmitter<number>();

  public enumActivityItem: typeof enumActivityItem = enumActivityItem;

  constructor(private translateService: TranslateService) {}

  public get normalizedLength(): number {
    return this.package.lengthUnit === enumLengthUnit.METER
      ? precisionRound(this.package.dimensions.length * METRE_CENTIMETER_RATIO)
      : this.package.dimensions.length;
  }

  public get normalizedWidth(): number {
    return this.package.lengthUnit === enumLengthUnit.METER
      ? precisionRound(this.package.dimensions.width * METRE_CENTIMETER_RATIO)
      : this.package.dimensions.width;
  }

  public get normalizedHeight(): number {
    return this.package.lengthUnit === enumLengthUnit.METER
      ? precisionRound(this.package.dimensions.height * METRE_CENTIMETER_RATIO)
      : this.package.dimensions.height;
  }

  public get typeName(): string {
    return this.translateService.instant('PACKAGE_TYPE.' + enumPackageType[this.package.type]);
  }

  public get lengthName(): string {
    const lengthUnit: number = this.package.lengthUnit === enumLengthUnit.METER ? enumLengthUnit.CENTIMETER : this.package.lengthUnit;

    return this.translateService.instant('LENGTH_UNIT.' + enumLengthUnit[lengthUnit]);
  }

  public get weightName(): string {
    return this.translateService.instant('WEIGHT_UNIT.' + enumWeightUnit[this.package.weightUnit]);
  }

  public get isNotModified(): boolean {
    return enumActivityItem.NOT_MODIFIED === (this.package as ManagementPackageModel)?.action;
  }

  public get isOutdated(): boolean {
    return enumActivityItem.PREVIOUS === (this.package as ManagementPackageModel)?.action;
  }

  public get isRemoved(): boolean {
    return enumActivityItem.DELETED === (this.package as ManagementPackageModel)?.action;
  }

  public get isShowPallet(): boolean {
    return this.package.type === enumPackageType.PALLET;
  }

  public get isShowPalletStandardInput(): boolean {
    return (
      this.isShowPallet &&
      [enumPalletStandard.GMA, enumPalletStandard.EUR_1, enumPalletStandard.EUR_2].includes(this.package.palletStandard)
    );
  }

  public get palletStandard(): boolean {
    return this.translateService.instant('PALLET_TYPES.' + enumPalletStandard[this.package.palletStandard]);
  }
}
